import React, { useEffect, useState } from 'react';
import { Form, InputNumber, Select, Button, message, Space } from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosInstance';
import config from '../utils/config';
import axios from "axios";

const { Option } = Select;

interface Party {
    id: number;
    party_name: string;
    party_logo: string;
}

const EditResultPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [parties, setParties] = useState<Party[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        fetchParties();
        fetchResult(); // eslint-disable-next-line
    }, []);

    const fetchParties = async () => {
        try {
            const response = await axiosInstance.get(config.api.endpoints.parties, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('access_token')}`,
                },
            });
            setParties(response.data);
        } catch (error) {
            console.error('Error fetching parties:', error);
            message.error('Failed to fetch political parties.');
        }
    };

    const fetchResult = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(`${config.api.endpoints.results}/${id}`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('access_token')}`,
                },
            });
            const resultData = response.data;
            form.setFieldsValue({
                party_votes: resultData.party_votes,
                total_vote_cast: resultData.total_vote_cast,
                total_valid_votes: resultData.total_valid_votes,
                total_rejected_ballots: resultData.total_rejected_ballots,
            });
        } catch (error) {
            console.error('Error fetching result:', error);
            message.error('Failed to fetch result details.');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (values: any) => {
        setLoading(true);
        try {
            // Fetch existing data to include all required fields
            const existingResultResponse = await axiosInstance.get(`${config.api.endpoints.results}/${id}`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('access_token')}`,
                },
            });

            const existingResult = existingResultResponse.data;

            const payload = {
                agent_id: existingResult.agent_id, // Include agent ID
                constituency_id: existingResult.constituency_id, // Include constituency ID
                constituency_name: existingResult.constituency_name, // Include constituency name
                const_code: existingResult.const_code, // Include constituency code
                pinksheet_id: existingResult.pinksheet_id, // Include pinksheet ID
                total_valid_votes: values.total_valid_votes,
                total_rejected_ballots: values.total_rejected_ballots,
                total_vote_cast: values.total_vote_cast,
                election_type: existingResult.election_type,
                status: existingResult.status,
                submitted_from_loc: existingResult.submitted_from_loc, // Include location
                parties: values.party_votes.map((vote: any) => ({
                    party_id: vote.party_id,
                    votes: vote.votes,
                })),
            };


            // Make the API call
            await axiosInstance.put(`${config.api.endpoints.results}/${id}`, payload, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('access_token')}`,
                },
            });

            message.success('Result updated successfully!');
            navigate('/results');
        } catch (err: unknown) {
            console.error('Error updating result:', err);

            // Error handling
            if (axios.isAxiosError(err)) {
                if (err.response?.data?.detail) {
                    message.error(err.response.data.detail);
                } else {
                    message.error(err.message || 'Failed to update result.');
                }
            } else {
                message.error('An unexpected error occurred.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ padding: '20px' }}>
            <h2>Edit Election Result</h2>
            <Form form={form} layout="vertical" onFinish={handleSubmit}>
                <Form.Item
                    name="total_valid_votes"
                    label="Total Valid Votes"
                    rules={[{ required: true, message: 'Total valid votes are required' }]}
                >
                    <InputNumber min={0} placeholder="Enter total valid votes" style={{ width: '100%' }} />
                </Form.Item>

                <Form.Item
                    name="total_rejected_ballots"
                    label="Total Rejected Ballots"
                    rules={[{ required: true, message: 'Total rejected ballots are required' }]}
                >
                    <InputNumber min={0} placeholder="Enter total rejected ballots" style={{ width: '100%' }} />
                </Form.Item>

                <Form.Item
                    name="total_vote_cast"
                    label="Total Votes Cast"
                    rules={[{ required: true, message: 'Total votes cast are required' }]}
                >
                    <InputNumber min={0} placeholder="Enter total votes cast" style={{ width: '100%' }} />
                </Form.Item>

                <Form.List name="party_votes">
                    {(fields, { remove }) => (
                        <>
                            {fields.map(({ key, name, ...restField }) => (
                                <Space key={key} align="baseline" style={{ marginBottom: '8px' }}>
                                    <Form.Item
                                        {...restField}
                                        name={[name, 'party_id']}
                                        rules={[{ required: true, message: 'Party is required' }]}
                                    >
                                        <Select
                                            placeholder="Select Party"
                                            style={{ width: 200 }}
                                            disabled
                                            optionLabelProp="label"
                                        >
                                            {parties.map((party) => (
                                                <Option key={party.id} value={party.id} label={party.party_name}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                        <img
                                                            src={party.party_logo}
                                                            alt={party.party_name}
                                                            style={{ width: 20, height: 20, borderRadius: '50%' }}
                                                        />
                                                        {party.party_name}
                                                    </div>
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>

                                    <Form.Item
                                        {...restField}
                                        name={[name, 'votes']}
                                        rules={[{ required: true, message: 'Please enter votes' }]}
                                    >
                                        <InputNumber min={0} placeholder="Votes" style={{ width: '120px' }} />
                                    </Form.Item>
                                </Space>
                            ))}
                        </>
                    )}
                </Form.List>

                <Form.Item>
                    <Button type="primary" htmlType="submit" loading={loading}>
                        Update Result
                    </Button>
                </Form.Item>
            </Form>
        </div>
    );
};

export default EditResultPage;